<script setup>
import { computed, ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...flowDynamicVariables.value];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const CONTEXT_OPTIONS = computed(() => [
  { value: 'contact',      label: t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.CONTACT') },
  { value: 'conversation', label: t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.CONVERSATION') },
]);

const contextType = computed(() => props.modelValue?.context_type ?? 'contact');
const variable    = computed(() => props.modelValue?.variable ?? '');

const openPicker   = ref(false);
const pickerSearch = ref('');

const filteredVars = computed(() => {
  const q = pickerSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q));
});

function togglePicker() {
  openPicker.value = !openPicker.value;
  pickerSearch.value = '';
}

function insertVar(key) {
  update({ variable: `{{${key}}}` });
  openPicker.value = false;
  pickerSearch.value = '';
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Contexto -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.CONTEXT') }}</span>
      <div class="flex flex-col gap-1">
        <label
          v-for="opt in CONTEXT_OPTIONS"
          :key="opt.value"
          class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors"
          :class="contextType === opt.value
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7'"
        >
          <input
            type="radio"
            class="sr-only"
            :value="opt.value"
            :checked="contextType === opt.value"
            @change="update({ context_type: opt.value })"
          />
          <span
            class="size-4 rounded-full border-2 flex items-center justify-center shrink-0"
            :class="contextType === opt.value ? 'border-n-brand' : 'border-n-slate-7'"
          >
            <span v-if="contextType === opt.value" class="size-2 rounded-full bg-n-brand" />
          </span>
          <span class="text-sm text-n-slate-12">{{ opt.label }}</span>
        </label>
      </div>
    </div>

    <!-- Variável -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ contextType === 'contact' ? t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.ID_OF_CONTACT') : t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.ID_OF_CONVERSATION') }}
      </span>
      <div class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            type="text"
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9 font-mono"
            :placeholder="contextType === 'contact' ? '{{variables.pedido.contact_id}}' : '{{variables.pedido.conversation_id}}'"
            :value="variable"
            @input="update({ variable: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="togglePicker"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="openPicker"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="pickerSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="insertVar(v.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
      <p class="text-xs text-n-slate-9">
        {{ contextType === 'contact' ? t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.HELP_CONTACT') : t('FLOW_BUILDER.ACTION_FORMS.SET_CONTEXT.HELP_CONVERSATION') }}
      </p>
    </div>

  </div>
</template>
